/**
 * BulkActionsApi - API service for bulk mention review operations
 *
 * Connects to backend endpoints:
 * - POST /api/admin/mentions/bulk-review
 *
 * Backend controller: Backend/MentionedMe.API/Controllers/MentionReviewController.cs
 * Backend handler: Backend/MentionedMe.Application/Features/PostReview/Commands/BulkReviewMentionsCommandHandler.cs
 */
import { apiClient } from './ApiClient';

/**
 * Base path for bulk actions API endpoints
 */
const BASE_PATH = '/api/admin/mentions';

/**
 * Request body for bulk review operation
 *
 * Maps to: Backend BulkReviewRequest
 */
export interface BulkReviewMentionsRequest {
  /**
   * Array of mention IDs to review (Guid → string)
   */
  mentionIds: string[];

  /**
   * Action to perform on selected mentions
   * - "Approve": Approve all selected mentions
   * - "Reject": Reject all selected mentions
   */
  action: 'Approve' | 'Reject';

  /**
   * Optional admin notes applied to all mentions
   */
  adminNotes?: string;

  /**
   * Rejection reason (required when action = "Reject")
   */
  rejectionReason?: string;
}

/**
 * Error details for a single failed mention in bulk operation
 *
 * Maps to: Backend BulkReviewError
 */
export interface BulkReviewError {
  /**
   * Mention ID that failed (Guid → string)
   */
  mentionId: string;

  /**
   * Error message describing why the mention failed
   */
  errorMessage: string;
}

/**
 * Response from bulk review operation
 *
 * Maps to: Backend BulkReviewResult
 */
export interface BulkReviewMentionsResponse {
  /**
   * Total number of mentions requested for review
   */
  totalRequested: number;

  /**
   * Number of mentions successfully reviewed
   */
  successCount: number;

  /**
   * Number of mentions that failed to review
   */
  failureCount: number;

  /**
   * List of errors for failed mentions
   */
  errors: BulkReviewError[];
}

/**
 * BulkActionsApi class
 *
 * Provides type-safe methods for bulk mention review operations
 */
export class BulkActionsApi {
  /**
   * Bulk review multiple mentions (approve or reject)
   *
   * @param request - Bulk review request with mention IDs and action
   * @returns Promise<BulkReviewMentionsResponse> - Result with success/failure counts
   *
   * @throws {Error} 400 Bad Request - Validation failures (empty array, invalid action, missing rejection reason)
   * @throws {Error} 401 Unauthorized - Missing/invalid auth token
   *
   * @example
   * // Bulk approve
   * const result = await bulkActionsApi.bulkReviewMentions({
   *   mentionIds: ['123e4567-e89b-12d3-a456-426614174000', '223e4567-e89b-12d3-a456-426614174000'],
   *   action: 'Approve',
   *   adminNotes: 'Bulk approved from post detail view'
   * });
   *
   * @example
   * // Bulk reject
   * const result = await bulkActionsApi.bulkReviewMentions({
   *   mentionIds: ['123e4567-e89b-12d3-a456-426614174000'],
   *   action: 'Reject',
   *   rejectionReason: 'WrongBusiness',
   *   adminNotes: 'Not the correct business'
   * });
   *
   * @example
   * // Handle partial success
   * const result = await bulkActionsApi.bulkReviewMentions({
   *   mentionIds: selectedIds,
   *   action: 'Approve'
   * });
   *
   * if (result.failureCount > 0) {
   *   console.warn(`${result.successCount}/${result.totalRequested} succeeded`);
   *   result.errors.forEach(error => {
   *     console.error(`Failed to review ${error.mentionId}: ${error.errorMessage}`);
   *   });
   * }
   */
  async bulkReviewMentions(
    request: BulkReviewMentionsRequest
  ): Promise<BulkReviewMentionsResponse> {
    const url = `${BASE_PATH}/bulk-review`;
    return apiClient.post<BulkReviewMentionsResponse>(url, request);
  }
}

/**
 * Default bulk actions API instance
 */
export const bulkActionsApi = new BulkActionsApi();
